(() => {
  'use strict';
  if (customElements.get('apex-devil-eyes')) return;
  const SIZE = 1254;
  const AREA = { x: 422, y: 460, width: 404, height: 130 };
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  // Only the interiors of the two eyes are painted. The original illustration,
  // including its black eye outlines, remains the visible image underneath.
  const EYES = [
    { path: new Path2D('M425.5 481.5L426.5 468.5L427.5 468.5L437.5 474.5L465.5 492.5L552.5 548.5L556.5 552.5L556.5 554L555.5 556.5L553.5 560L544.5 570L540.5 573.5L537.5 575.5L530.5 579.5L525.5 581.5L519.5 583.5L515.5 584.5L506.5 585.5L502.5 585.5L492.5 584.5L484.5 582.5L481.5 581.5L467.5 574.5L463.5 571.5L458.5 567.5L452.5 561.5L447.5 555.5L441.5 546L435.5 533.5L433.5 528.5L430.5 519L429.5 514.5L427.5 504L426.5 496.5L425.5 483.5Z'), x: 507, y: 513, rx: 18, ry: 41, slope: 84 / 130, lidX: 426.5, lidY: 468.5, lidWidth: 130, skin: '#e30c10' },
    { path: new Path2D('M674.5 551.5L773.5 490.5L786.5 482.5L796.5 476.5L808.5 469.5L816.5 465.5L818.5 467.5L819.5 470.5L820.5 481.5L820.5 485.5L819.5 499L817.5 511.5L816.5 517L814.5 524.5L812.5 531L810.5 536.5L807.5 542.5L803.5 549.5L798.5 556.5L786.5 568.5L781.5 572.5L778.5 574.5L768.5 579.5L763.5 581.5L760.5 582.5L756.5 583.5L751.5 584.5L743.5 585.5L728.5 585.5L720.5 584.5L716.5 583.5L709.5 581.5L704.5 579.5L694.5 574.5L690.5 571L685.5 566.5L678.5 558.5L674.5 552.5Z'), x: 738, y: 512, rx: 19, ry: 42, slope: -86 / 142, lidX: 674.5, lidY: 551.5, lidWidth: 142, skin: '#ec0d10' }
  ];

  class ApexDevilEyes extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.abort = new AbortController();
      this.poster = this.querySelector('img');
      this.canvas = this.querySelector('canvas');
      this.button = this.querySelector('[data-devil-eyes-pause]');
      this.ctx = this.canvas?.getContext('2d', { alpha: true });
      if (!this.ctx || !this.poster) return;
      this.ready = false; this.loading = false; this.paused = false; this.visible = false;
      this.clock = 0; this.frames = 0; this.frameId = null;
      this.nextBlink = 2200 + Math.random() * 1200; this.blinkStart = -Infinity;
      this.gaze = { x: 0, y: 0 }; this.target = { x: 0, y: 0 }; this.rect = null;
      this.removeAttribute('data-ready'); this.removeAttribute('data-paused'); this.removeAttribute('data-fallback');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.onFrame = this.onFrame.bind(this);
      const signal = this.abort.signal;
      document.addEventListener('pointermove', event => {
        if (!this.canPlay() || event.pointerType === 'touch') return;
        const bounds = this.rect || (this.rect = this.poster.getBoundingClientRect());
        this.target.x = clamp((event.clientX - bounds.left - bounds.width * 0.5) / Math.max(1, bounds.width * 0.7), -1, 1);
        this.target.y = clamp((event.clientY - bounds.top - bounds.height * 0.42) / Math.max(1, bounds.height * 0.7), -1, 1);
      }, { passive: true, signal });
      document.documentElement.addEventListener('pointerleave', () => { this.target.x = 0; this.target.y = 0; }, { signal });
      window.addEventListener('scroll', () => { this.rect = null; }, { passive: true, signal });
      document.addEventListener('visibilitychange', () => this.syncPlayback(), { signal });
      this.button?.addEventListener('click', () => this.setPaused(!this.paused), { signal });
      this.reduced.addEventListener('change', () => {
        this.toggleAttribute('data-reduced', this.reduced.matches);
        if (!this.reduced.matches && this.visible) this.scheduleLoad();
        this.syncPlayback();
      }, { signal });
      this.toggleAttribute('data-reduced', this.reduced.matches || this.dataset.motion === 'false');
      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(this.canvas);
      // Keep the lower mascot lazy: initialize only when it reaches the viewport.
      this.intersectionObserver = new IntersectionObserver(entries => {
        this.visible = entries.some(entry => entry.isIntersecting);
        this.rect = null;
        if (this.visible) this.scheduleLoad();
        this.syncPlayback();
      }, { threshold: 0.01 });
      this.intersectionObserver.observe(this);
    }

    disconnectedCallback() {
      this.connected = false; this.abort?.abort();
      this.resizeObserver?.disconnect(); this.intersectionObserver?.disconnect();
      cancelAnimationFrame(this.frameId); this.frameId = null;
    }

    async scheduleLoad() {
      if (!this.connected || this.loading || this.ready || this.reduced.matches || this.dataset.motion === 'false') return;
      this.loading = true;
      try {
        await this.poster.decode();
        if (!this.connected) return;
        if (this.poster.naturalWidth !== SIZE || this.poster.naturalHeight !== SIZE) throw new Error('Unexpected mascot dimensions');
        this.ready = true; this.resize(); this.render();
        this.setAttribute('data-ready', ''); this.syncPlayback();
      } catch {
        if (this.connected) this.setAttribute('data-fallback', '');
      } finally { this.loading = false; }
    }

    resize() {
      this.rect = null;
      const bounds = this.canvas.getBoundingClientRect();
      if (!bounds.width) return;
      const dpr = Math.min(devicePixelRatio || 1, 2);
      const width = Math.max(1, Math.round(bounds.width * dpr));
      const height = Math.max(1, Math.round(bounds.height * dpr));
      if (this.canvas.width !== width || this.canvas.height !== height) { this.canvas.width = width; this.canvas.height = height; }
      if (this.ready) this.render();
    }

    canPlay() {
      return this.ready && this.connected && this.visible && !this.paused && !this.reduced.matches && this.dataset.motion !== 'false' && !document.hidden;
    }

    syncPlayback() {
      const play = this.canPlay();
      this.toggleAttribute('data-playing', play);
      if (play && this.frameId === null) {
        this.lastTick = null; this.lastRender = -Infinity;
        this.frameId = requestAnimationFrame(this.onFrame);
      } else if (!play) {
        cancelAnimationFrame(this.frameId); this.frameId = null; this.lastTick = null;
      }
    }

    setPaused(value) {
      this.paused = value; this.toggleAttribute('data-paused', value);
      this.button?.setAttribute('aria-pressed', String(value));
      this.button?.setAttribute('aria-label', value ? 'Resume mascot eye animation' : 'Pause mascot eye animation');
      this.syncPlayback();
    }

    onFrame(timestamp) {
      this.frameId = requestAnimationFrame(this.onFrame);
      if (timestamp - this.lastRender < 1000 / 30 - 1) return;
      const elapsed = this.lastTick === null ? 0 : Math.min(80, timestamp - this.lastTick);
      this.lastTick = timestamp; this.lastRender = timestamp; this.clock += elapsed;
      const follow = 1 - Math.exp(-elapsed / 170);
      this.gaze.x += (this.target.x - this.gaze.x) * follow;
      this.gaze.y += (this.target.y - this.gaze.y) * follow;
      if (this.clock >= this.nextBlink) {
        this.blinkStart = this.clock; this.nextBlink = this.clock + 3500 + Math.random() * 3500;
      }
      this.render(); this.frames++;
    }

    drawEye(eye, open) {
      const c = this.ctx;
      c.save(); c.clip(eye.path);
      c.fillStyle = eye.skin; c.fillRect(AREA.x, AREA.y, AREA.width, AREA.height);
      if (open > 0.015) {
        c.translate(0, 579); c.scale(1, open); c.translate(0, -579);
        const white = c.createLinearGradient(0, 465, 0, 584);
        white.addColorStop(0, '#f4f4f5'); white.addColorStop(1, '#fafafa');
        c.fillStyle = white; c.fill(eye.path);
        const px = eye.x + this.gaze.x * 12, py = eye.y + this.gaze.y * 5;
        c.fillStyle = '#070708'; c.beginPath();
        c.ellipse(px, py, eye.rx, eye.ry, 0, 0, Math.PI * 2); c.fill();
        c.save(); c.clip();
        // Place each catchlight within the visible part of its pupil.
        const lightX = px + 8;
        const halfHeight = eye.ry * Math.sqrt(1 - 64 / (eye.rx * eye.rx));
        const top = Math.max(py - halfHeight, eye.lidY + (lightX - eye.lidX) * eye.slope + 5);
        const bottom = py + halfHeight;
        const visible = Math.max(0, bottom - top);
        const lightY = top + visible * 0.42, size = Math.min(1, visible / 16);
        c.fillStyle = '#fafafa'; c.beginPath();
        c.ellipse(lightX, lightY, 2 * size, 4 * size, 0, 0, Math.PI * 2); c.fill();
        c.restore();
      }
      c.restore();
      // Finish outside the clip so its antialiased edge cannot reveal the
      // original white eye underneath, especially where a pupil meets the lid.
      c.save(); c.strokeStyle = '#070708'; c.lineWidth = 3;
      c.lineJoin = 'round'; c.stroke(eye.path); c.restore();
    }

    render() {
      if (!this.ready) return;
      const c = this.ctx;
      c.setTransform(1, 0, 0, 1, 0, 0); c.clearRect(0, 0, this.canvas.width, this.canvas.height);
      c.setTransform(this.canvas.width / AREA.width, 0, 0, this.canvas.height / AREA.height, 0, 0);
      c.translate(-AREA.x, -AREA.y);
      const elapsed = this.clock - this.blinkStart;
      const blink = elapsed >= 0 && elapsed < 200 ? Math.pow(Math.sin(elapsed / 200 * Math.PI), 0.8) : 0;
      EYES.forEach(eye => this.drawEye(eye, 1 - blink));
    }
  }
  customElements.define('apex-devil-eyes', ApexDevilEyes);
})();
